/**
 * theme.ts - デザイントークン
 *
 * プロジェクト全体で使用する色、サイズ、余白などの基準値を定義
 * スタイル定義はここから値を参照することで一貫性を保つ
 *
 * Usage:
 * import { theme } from '@/styles';
 * background: theme.colors.background.primary,
 */

// ========================================
// カラーパレット
// ========================================

export const colors = {
  // 背景色
  background: {
    primary: '#1a1a3a',
    secondary: '#2a2a4a',
    tertiary: '#0a0a1a',
    card: '#16213e',
    overlay: 'rgba(0, 0, 0, 0.85)',
    overlayLight: 'rgba(0, 0, 0, 0.5)',
  },

  // テキスト色
  text: {
    primary: '#ffffff',
    secondary: '#a0a0c0',
    muted: '#666666',
    disabled: '#444444',
  },

  // アクセントカラー
  accent: {
    purple: '#6b4ce6',
    purpleLight: '#9c6bff',
    purpleDark: '#4a3399',
    gold: '#ffd700',
    blue: '#6b9eff',
    green: '#6be66b',
    red: '#ff6b6b',
    cyan: '#00ffff',
  },

  // 属性カラー（constants.tsのATTRIBUTE_COLORSと連携）
  attribute: {
    fire: '#ff6b6b',
    fireGlow: '#ff6b35',
    water: '#4da6ff',
    waterGlow: '#4da6ff',
    light: '#ffd700',
    lightGlow: '#ffd700',
    dark: '#9933ff',
    darkGlow: '#9933ff',
    future: '#00ffff',
    futureGlow: '#00ffff',
    primitive: '#7cfc00',
    primitiveGlow: '#7cfc00',
    none: '#888888',
    noneGlow: '#c0c0c0',
  },

  // UI状態
  state: {
    success: '#4caf50',
    warning: '#ff9800',
    error: '#f44336',
    info: '#2196f3',
  },

  // ボーダー
  border: {
    default: '#4a4a6a',
    light: 'rgba(255, 255, 255, 0.1)',
    dark: 'rgba(0, 0, 0, 0.3)',
    accent: '#6b4ce6',
    gold: '#ffd700',
  },
} as const;

// ========================================
// スペーシング
// ========================================

export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
  xxl: 48,
} as const;

// ========================================
// ボーダー半径
// ========================================

export const borderRadius = {
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  round: '50%',
} as const;

// ========================================
// シャドウ
// ========================================

export const shadows = {
  sm: '0 2px 4px rgba(0, 0, 0, 0.3)',
  md: '0 4px 12px rgba(0, 0, 0, 0.4)',
  lg: '0 8px 24px rgba(0, 0, 0, 0.5)',
  xl: '0 20px 60px rgba(0, 0, 0, 0.5)',
  glow: {
    purple: '0 0 30px rgba(107, 76, 230, 0.4)',
    purpleStrong: '0 0 50px rgba(107, 76, 230, 0.5)',
    gold: '0 0 30px rgba(255, 215, 0, 0.4)',
    blue: '0 0 30px rgba(107, 158, 255, 0.4)',
    green: '0 0 30px rgba(107, 230, 107, 0.4)',
    red: '0 0 30px rgba(255, 107, 107, 0.4)',
  },
} as const;

// ========================================
// タイポグラフィ
// ========================================

export const typography = {
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 20,
    xl: 24,
    xxl: 32,
  },
  fontWeight: {
    normal: 400,
    medium: 500,
    bold: 700,
  },
  lineHeight: {
    tight: 1.2,
    normal: 1.5,
    relaxed: 1.8,
  },
} as const;

// ========================================
// トランジション
// ========================================

export const transitions = {
  fast: '0.15s ease',
  normal: '0.2s ease',
  slow: '0.3s ease',
} as const;

// ========================================
// z-index
// ========================================

export const zIndex = {
  base: 0,
  dropdown: 100,
  sticky: 500,
  modal: 1000,
  tooltip: 1100,
  toast: 1200,
  max: 9999,
} as const;

// ========================================
// 統合エクスポート
// ========================================

export const theme = {
  colors,
  spacing,
  borderRadius,
  shadows,
  typography,
  transitions,
  zIndex,
} as const;

// ========================================
// 型定義
// ========================================

export type Theme = typeof theme;
export type ThemeColors = typeof colors;
export type ThemeSpacing = typeof spacing;
export type ThemeBorderRadius = typeof borderRadius;
export type ThemeShadows = typeof shadows;
export type ThemeTypography = typeof typography;
export type ThemeTransitions = typeof transitions;
export type ThemeZIndex = typeof zIndex;

export default theme;
